import type React from 'react';
import { lazy, useEffect, useMemo, useRef } from 'react';

import { ApolloProvider } from '@apollo/client';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BrowserRouter } from 'react-router-dom';

import Head from 'components/client/Head';
import { getEnvConfig } from 'config/environment';
import { client } from 'config/graphql/client';
import { setup } from 'config/initialState';
import AnalyticsProvider from 'containers/analytics';
import ErrorBoundary from 'containers/ErrorBoundary';
import ToastContainer from 'containers/toast/ToastContainer';
import UserManagementContainer from 'containers/userManagement/UserManagementContainer';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import MainRouter from 'router/MainRouter';
import 'styles/main.css';
import { theme } from 'theme/theme';
import { themeV2 } from 'theme/themeV2';

import('array.prototype.tosorted/auto');
import('array.prototype.findlast/auto');

export const muiCache = createCache({
  key: 'mui',
  prepend: true,
});

const JotaiDevTools = lazy(() => import('./JotaiDevTools'));

const App: React.FC<React.PropsWithChildren<unknown>> = () => {
  const setupInitialized = useRef(false);
  const BASENAME = getEnvConfig().REACT_APP_ROUTER_BASENAME || '';
  const { themeName } = useSwitchTheme();
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (!setupInitialized.current) {
      setupInitialized.current = true;
      setup();
    }
  }, [setupInitialized]);

  useEffect(() => {
    const loadJotaiDevToolsStyles = async () => {
      await import('jotai-devtools/styles.css');
    };
    isDev && loadJotaiDevToolsStyles();
  }, [isDev]);

  useEffect(() => {
    document.querySelector('body').className = themeV2;
  }, []);

  const children = useMemo(
    () => (
      <ErrorBoundary>
        <ApolloProvider client={client}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CacheProvider value={muiCache}>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme()}>
                  <CssBaseline />
                  <BrowserRouter basename={BASENAME}>
                    <AnalyticsProvider>
                      <UserManagementContainer>
                        <DndProvider backend={HTML5Backend}>
                          <MainRouter />
                          <ToastContainer themeName={themeName} />
                        </DndProvider>
                      </UserManagementContainer>
                    </AnalyticsProvider>
                  </BrowserRouter>
                </ThemeProvider>
              </StyledEngineProvider>
            </CacheProvider>
          </LocalizationProvider>
        </ApolloProvider>
      </ErrorBoundary>
    ),
    [BASENAME, themeName],
  );

  return useMemo(
    () =>
      setupInitialized ? (
        <>
          <Head />
          {isDev ? (
            <>
              <JotaiDevTools />
              {children}
            </>
          ) : (
            children
          )}
        </>
      ) : null,
    [children, isDev, setupInitialized],
  );
};

export default App;
